import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { getEnv, getEnvDiagnostics } from '@/config/env';

/**
 * Component that displays a friendly error message if environment variables are missing
 * This prevents blank screens and helps diagnose deployment issues
 */
export function EnvErrorBoundary({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<ReturnType<typeof getEnvDiagnostics> | null>(null);

  useEffect(() => {
    try {
      // Try to access env config - this will throw if invalid
      getEnv();
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setDiagnostics(getEnvDiagnostics());
    }
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-4">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="w-6 h-6" />
            <h1 className="text-2xl font-bold">Configuration Error</h1>
          </div>
          
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="font-medium">{String(error?.message ?? error ?? 'Unknown error')}</p>
            <p className="text-sm text-muted-foreground">
              This error occurs when required environment variables are not set in your Vercel project settings.
            </p>
          </div>

          {diagnostics && (
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p className="font-medium text-sm">Diagnostics:</p>
              <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                <li>VITE_SUPABASE_URL: {diagnostics.hasSupabaseUrl ? '✓ Set' : '✗ Missing'}</li>
                <li>VITE_SUPABASE_ANON_KEY: {diagnostics.hasSupabaseKey ? `✓ Set (prefix: ${diagnostics.anonKeyPrefix})` : '✗ Missing'}</li>
                <li>VITE_SITE_URL: {diagnostics.hasSiteUrl ? '✓ Set' : '✗ Missing (optional)'}</li>
                <li>VITE_STRIPE_PRICE_ID: {diagnostics.hasStripePriceId ? '✓ Set' : '✗ Missing (optional)'}</li>
                {diagnostics.siteUrl && (
                  <li>Detected Site URL: {diagnostics.siteUrl}</li>
                )}
              </ul>
            </div>
          )}

          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm font-medium mb-2">How to fix:</p>
            <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
              <li>Go to your Vercel project dashboard</li>
              <li>Navigate to Settings → Environment Variables</li>
              <li>Add the missing variables listed above</li>
              <li>Redeploy your application</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

